// Module 10 — Cesium adapter public entry. Implements ROADMAP §10.1, R2/R9/R11.
// Frozen interface: docs/interfaces/viz.v1.md · Rule: .claude/rules/viz.md
// This is the ONLY file that binds the real CesiumJS module (a value import). Tests never import it,
// so the heavy GL module never loads in node — they inject a `CesiumLike` fake into makeAdapter
// instead. createCesiumAdapter wires real Cesium + the `sim` singleton store behind the frozen port.
// Boundary: imports only `cesium` (npm), `state` (allowed dep), `domain` (via contract), same-module.
import * as Cesium from "cesium";
import { makeAdapter } from "./adapter";
import type { DisplayExtras, VizDebug } from "./adapter";
import { createEffectsEncoder } from "./effects/index";
import { sim } from "../state/index";
import type { CesiumLike } from "./cesium-api";
import type { CesiumAdapter } from "./contract";
import type { Generator } from "../domain/index";

// `opts.generators` is the app-injected static generator list the 10.6 plant encoder needs — they are
// absent from the frozen VizModel by design (mount() carries only nodes/edges/loads), so the composition
// root (Module 12) hands them in here. Optional + additive: the no-arg call is unchanged. This rides the
// NON-contract EffectsEncoder seam, not the frozen port — see viz.v1.md migration note (2026-06-05).
// `opts.display` (v3): Debug 3 display-fidelity extras (facility parcels / connection points / pruned
// wiring) — same non-contract seam, same rationale.
// Returns the frozen CesiumAdapter PLUS the non-contract VizDebug seam (introspection + the date/sun
// controls the index.html clock UI drives) — VizDebug is explicitly non-contract, so exposing it here
// does not widen the frozen viz.v1 surface (the app uses _debug; modules still depend only on the port).
export function createCesiumAdapter(opts?: { generators?: Generator[]; display?: DisplayExtras }): CesiumAdapter & VizDebug {
  // localized structural cast: the real Cesium namespace is a superset of the CesiumLike seam.
  const cesium = Cesium as unknown as CesiumLike;
  // 10.6 visual encoding plugs in behind the SAME frozen port via the EffectsEncoder seam.
  const gens = opts?.generators;
  return makeAdapter(cesium, sim, createEffectsEncoder(gens ? { generators: gens } : undefined), opts?.display);
}

export type {
  QualityTier, BasemapKind, VizModel, PickSel, EffectsEncoder, EffectsInput, CesiumAdapter,
} from "./contract";
export { makeAdapter } from "./adapter";
export type { VizDebug, DisplayExtras } from "./adapter";
export type { CesiumLike } from "./cesium-api";
